<template>
  <div class="pin-input-wrapper">
    <div class="grid grid-flow-col auto-cols-fr" :class="gapClass" @paste.prevent="onPaste">
      <input
        v-for="i in length"
        :key="i"
        :ref="
          el => {
            if (el) pinRefs[i - 1] = el;
          }
        "
        :value="getDisplayValue(i - 1)"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="1"
        :class="[
          'pin-input',
          inputClass,
          hasError ? errorClass : '',
          disabled ? 'opacity-60 cursor-not-allowed' : '',
        ]"
        :disabled="disabled"
        :aria-label="`PIN digit ${i}`"
        @input="onInput(i - 1, $event)"
        @keydown.backspace="onBackspace(i - 1, $event)"
        @keydown.enter.prevent="submit"
        @focus="onFocus(i - 1)"
        :autofocus="i === 1"
      />
    </div>
  </div>
</template>

<script>
import { Keyboard } from '@capacitor/keyboard';

export default {
  name: 'PinInput',

  props: {
    length: { type: Number, default: 4 },
    modelValue: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
    autoSubmit: { type: Boolean, default: true },
    gapClass: { type: String, default: 'gap-x-4' },
    inputClass: {
      type: String,
      default:
        'w-full h-[72px] text-center text-[1.5rem] bg-[#FFFFFF] font-semibold border-2 rounded-xl focus:outline-none focus:border-[#0088FF] focus:ring-2 focus:ring-[#0088FF] transition-all border-[#D8DADC]',
    },
    errorClass: { type: String, default: '!border-red-500' },
  },

  emits: ['update:modelValue', 'complete', 'focus'],

  data() {
    return {
      digits: [],
      pinRefs: [],
      lastSubmittedValue: null,
    };
  },

  computed: {
    isComplete() {
      return this.digits.length === this.length && this.digits.every(d => d !== '');
    },

    value() {
      return this.digits.join('');
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.value) {
          this.setDigits(newVal);
        }
      },
    },

    value(newVal) {
      this.$emit('update:modelValue', newVal);
      if (newVal !== this.lastSubmittedValue && !this.isComplete) {
        this.lastSubmittedValue = null;
      }
    },

    length(newLength) {
      if (this.value.length > newLength) {
        this.setDigits(this.value.slice(0, newLength));
      } else if (this.digits.length !== newLength) {
        const currentDigits = [...this.digits];
        this.digits = Array.from({ length: newLength }, (_, i) => currentDigits[i] || '');
      }
    },
  },

  mounted() {
    this.initializeDigits();
  },

  methods: {
    getDisplayValue(index) {
      // Display dot (•) if digit exists, otherwise empty
      return this.digits[index] ? '•' : '';
    },

    async clearAndFocusFirst() {
      this.clear();
      await this.showKeyboard();
    },

    clear() {
      this.digits = Array.from({ length: this.length }, () => '');
      this.lastSubmittedValue = null;
      this.$nextTick(this.focusFirst);
    },

    initializeDigits() {
      this.digits = Array.from({ length: this.length }, () => '');
      this.$nextTick(this.focusFirst);
    },

    setDigits(value) {
      const str = String(value || '').replace(/\D/g, '');
      this.digits = Array.from({ length: this.length }, (_, i) => str[i] || '');

      // Update display values to show dots
      this.$nextTick(() => {
        this.pinRefs.forEach((ref, i) => {
          if (ref && this.digits[i]) {
            ref.value = '•';
          }
        });
      });

      if (this.autoSubmit && this.isComplete) {
        this.submit();
      }
    },

    async focusFirst() {
      await this.$nextTick();

      if (this.pinRefs[0]) {
        this.pinRefs[0].focus();
        await this.showKeyboard();
      }
    },

    focusIndex(index) {
      if (this.pinRefs[index]) {
        this.pinRefs[index].focus();
      }
    },

    async showKeyboard() {
      try {
        await Keyboard.show();
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Keyboard.show() only works on native device');
        }
      }
    },

    onInput(index, event) {
      const inputValue = event.target.value;

      // Handle backspace - if input is empty, clear the digit
      if (inputValue === '') {
        this.digits[index] = '';
        if (index > 0) {
          this.focusIndex(index - 1);
        }
        return;
      }

      // Extract numeric value
      const val = inputValue.replace(/\D/g, '');
      if (val) {
        // Store the actual digit value
        this.digits[index] = val.slice(-1);

        // Update display to show dot
        this.$nextTick(() => {
          if (this.pinRefs[index]) {
            this.pinRefs[index].value = '•';
          }
        });

        if (index < this.length - 1) {
          this.focusIndex(index + 1);
        }
      }

      if (this.autoSubmit && this.isComplete) {
        this.submit();
      }
    },

    onBackspace(index, event) {
      // Clear current digit
      this.digits[index] = '';

      // Update display
      this.$nextTick(() => {
        if (this.pinRefs[index]) {
          this.pinRefs[index].value = '';
        }
      });

      // Move focus to previous input if current is empty
      if (!this.digits[index] && index > 0) {
        event.preventDefault();
        this.focusIndex(index - 1);
      }
      this.lastSubmittedValue = null;
    },

    onPaste(e) {
      const str = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
      if (!str) return;

      const pasted = str.slice(0, this.length).split('');
      pasted.forEach((d, i) => {
        this.digits[i] = d;
      });

      // Update display values to show dots
      this.$nextTick(() => {
        pasted.forEach((_, i) => {
          if (this.pinRefs[i] && this.digits[i]) {
            this.pinRefs[i].value = '•';
          }
        });
      });

      const lastIndex = Math.min(pasted.length - 1, this.length - 1);
      this.focusIndex(lastIndex);

      if (this.autoSubmit && this.isComplete) {
        this.submit();
      }
    },

    onFocus(index) {
      this.$emit('focus', index);
    },

    // ===== Submission =====
    submit() {
      if (!this.isComplete) return;

      const v = this.value;
      if (this.lastSubmittedValue !== v) {
        this.lastSubmittedValue = v;
        this.$emit('complete', v);
      }
    },
  },
};
</script>

<style scoped>
.pin-input::-webkit-inner-spin-button,
.pin-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* 🔧 NEW: Better focus styles for accessibility */
.pin-input:focus {
  outline: none;
}

/* Ensure text is centered and properly displayed */
.pin-input {
  letter-spacing: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
</style>
